import React, {useCallback, useEffect, useRef, useState} from 'react';
import analytics from '@react-native-firebase/analytics';

import {
  Container,
  InstructionsContainer,
  Instruction,
  CaptureButton,
  Footer,
  CaptureButtonIcon,
  CaptureInstruction,
  SuccessContainer,
  SuccessText,
  MaskImage,
  ContainerCamera,
  CloseButton,
  CloseButtonIcon,
  CloseButtonContainer,
  PreviewImage,
  PreviewOverlay,
  PreviewButtonsRow,
  PreviewButton,
  PreviewButtonText,
  PreviewButtonIcon,
  GalleryPreviewImage,
  GalleryPreviewWrapper,
  TopControlsRow,
  FocusIndicator,
  CameraFooterRow,
  UploadButton,
  UploadButtonIcon,
  UploadButtonCircle,
  CaptureButtonCircle,
} from './styles';

import {
  Camera,
  useCameraDevice,
  useCameraPermission,
} from 'react-native-vision-camera';
import {Alert, ActivityIndicator} from 'react-native';
import ImagePicker from 'react-native-image-crop-picker';

import faceMask from '../../../assets/face-mask.png';
import identMask from '../../../assets/ident-mask.png';
import {useIsFocused, useNavigation, useRoute} from '@react-navigation/native';
import {
  enviarDocumentoCredito,
  TipoDocumento,
} from '../../../services/creditoMova';
import {SafeAreaView} from 'react-native-safe-area-context';
import {EmprestimoEligibilityGate} from '../../../components/shared/EmprestimoEligibilityGate';

type Step = 'doc-front' | 'doc-back' | 'selfie';

type RouteParams = {
  simulacao: any;
  installments_option_id: string;
  selectedOption: any;
  endereco_id: string;
  domicilio_id: string;
  tipo_doc?: 'rg' | 'cnh';
};

const STEP_CONFIG: Record<
  Step,
  {instruction: string; tipoDocumento: TipoDocumento; useBackCamera: boolean}
> = {
  'doc-front': {
    instruction: 'Capture uma foto da frente do seu documento',
    tipoDocumento: 'rg_cnh_frente',
    useBackCamera: true,
  },
  'doc-back': {
    instruction: 'Agora capture uma foto do verso do seu documento',
    tipoDocumento: 'rg_cnh_verso',
    useBackCamera: true,
  },
  selfie: {
    instruction: 'Capture uma foto do seu rosto de frente',
    tipoDocumento: 'selfie',
    useBackCamera: false,
  },
};

const STEP_ORDER: Step[] = ['doc-front', 'doc-back', 'selfie'];

export function Selfie() {
  const navigation = useNavigation();
  const route = useRoute();
  const params = route.params as RouteParams;

  const isFocused = useIsFocused();
  const {hasPermission, requestPermission} = useCameraPermission();
  useEffect(() => {
    if (!hasPermission) {
      requestPermission();
    }
  }, [hasPermission]);

  const [currentStep, setCurrentStep] = useState<Step>('doc-front');
  const [uploading, setUploading] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [previewSource, setPreviewSource] = useState<'camera' | 'gallery'>('camera');
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [focusPoint, setFocusPoint] = useState<{x: number; y: number} | null>(
    null,
  );
  const focusTimeout = useRef<NodeJS.Timeout | null>(null);

  const config = STEP_CONFIG[currentStep];
  const device = useCameraDevice(config.useBackCamera ? 'back' : 'front');

  useEffect(() => {
  analytics().logEvent('emprestimo_tela_captura_documento', {
    etapa: currentStep,
  });
}, [currentStep]);

  useEffect(() => {
    setIsCameraReady(false);
  }, [device?.id]);

  const cameraRef = useRef<Camera>(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const successTextRef = useRef<any>(null);

  const operacaoId = params?.simulacao?.operacao_id;
  const tipoDoc = params?.tipo_doc;

  const handleFocus = useCallback(
    async (e: any) => {
      if (!cameraRef.current || uploading) {
        return;
      }

      const {locationX, locationY} = e.nativeEvent;
      setFocusPoint({x: locationX, y: locationY});

      try {
        await cameraRef.current.focus({x: locationX, y: locationY});
      } catch {
        // some devices/cameras may not support focus — ignore
      }

      if (focusTimeout.current) {
        clearTimeout(focusTimeout.current);
      }
      focusTimeout.current = setTimeout(() => setFocusPoint(null), 1500);
    },
    [uploading],
  );

const takePicture = async () => {
  if (!cameraRef.current || uploading) {
    return;
  }

  try {
    const photo = await cameraRef.current.takePhoto();
    analytics().logEvent('emprestimo_capturou_foto', {
      etapa: currentStep,
      origem: 'camera',
    });
    setPreviewSource('camera');
    setPreviewUri(photo.path);
  } catch (error: any) {
    Alert.alert('Erro', 'Não foi possível capturar a foto. Tente novamente.');
  }
};

 const pickFromGallery = async () => {
  if (uploading) {
    return;
  }
  try {
    const image = await ImagePicker.openPicker({
      mediaType: 'photo',
      forceJpg: true,
      compressImageQuality: 0.9,
    });
    analytics().logEvent('emprestimo_capturou_foto', {
      etapa: currentStep,
      origem: 'galeria',
    });
    setPreviewSource('gallery');
    setPreviewUri(image.path);
  } catch (error: any) {
      if (error?.code !== 'E_PICKER_CANCELLED') {
        Alert.alert('Erro', 'Não foi possível selecionar a imagem.');
      }
    }
  };

  const handleRetake = () => {
    setPreviewUri(null);
  };

const handleConfirmPhoto = async () => {
  if (!previewUri) {
    return;
  }

  analytics().logEvent('emprestimo_confirmou_foto', {
    etapa: currentStep,
  });

  const photoUri = previewUri;
  setPreviewUri(null);

    // Show uploading state
    setUploading(true);
    setSuccessMessage('Enviando imagem...');
    setShowSuccess(true);
    successTextRef.current?.fadeInUp(1500);

    // Upload and wait for completion
    const success = await uploadSinglePhoto(currentStep, photoUri);

    if (!success) {
      // Upload failed — go back to camera to retake
      setUploading(false);
      setShowSuccess(false);
      return;
    }

    const currentIndex = STEP_ORDER.indexOf(currentStep);

   if (currentIndex === STEP_ORDER.length - 1) {
  // Last step — navigate after success message
  analytics().logEvent('emprestimo_documentos_enviados');
  setUploading(false);
  setSuccessMessage('Tudo certo! Documentos enviados com sucesso.');
  successTextRef.current?.fadeInUp(1500);

  setTimeout(() => {
    (navigation as any).navigate('EmprestimoConfirmacaoProposta', {
      ...params,
    });
  }, 3000);
  return;
}

    // Move to next step
    const nextStep = STEP_ORDER[currentIndex + 1];
    const nextConfig = STEP_CONFIG[nextStep];

    setUploading(false);
    setSuccessMessage('Imagem enviada! Vamos para a próxima etapa.');
    successTextRef.current?.fadeInUp(1500);

    setTimeout(() => {
      successTextRef.current?.fadeOutDown(1500).then(() => {
        setCurrentStep(nextStep);
        setSuccessMessage(nextConfig.instruction);

        successTextRef.current?.fadeInUp(1500).then(() => {
          setTimeout(() => {
            setShowSuccess(false);
          }, 2000);
        });
      });
    }, 2000);
  };

  const uploadSinglePhoto = async (
    step: Step,
    uri: string,
  ): Promise<boolean> => {
    const subtype =
      step !== 'selfie'
        ? tipoDoc === 'cnh'
          ? 'solicitante-CNH'
          : 'solicitante-RG'
        : undefined;

    try {
      await enviarDocumentoCredito(
        operacaoId,
        STEP_CONFIG[step].tipoDocumento,
        uri,
        subtype,
      );
      return true;
    } catch (error: any) {
      const body = error?.response?.data;
      const errorMessage: string | undefined =
        body?.error || body?.msg || body?.message || undefined;
      navigation.navigate('ConfirmationScreen', {
        title: 'Ocorreu um problema',

        text:
          errorMessage ||
          'Ocorreu um erro ao prosseguir com sua solicitação. Por favor, tente novamente mais tarde.',
        buttonAction: () => navigation.goBack(),
        closeIconAction: () => navigation.navigate('Initial'),
        buttonText: 'Tentar novamente',
        type: 'error',
        showSupport: true,
      });
      return false;
    }
  };

  if (device == null || !hasPermission) {
    return <Container />;
  }

  // Preview mode — show captured photo for review
  if (previewUri) {
    return (
      <Container>
        {previewSource === 'gallery' ? (
          <GalleryPreviewWrapper>
            <GalleryPreviewImage
              source={{uri: `file://${previewUri}`}}
              resizeMode="contain"
            />
          </GalleryPreviewWrapper>
        ) : (
          <PreviewImage
            source={{uri: `file://${previewUri}`}}
            resizeMode="cover"
          />
        )}
        <PreviewOverlay>
          <InstructionsContainer>
            <SafeAreaView>
              <Instruction>A foto ficou boa?</Instruction>
            </SafeAreaView>
          </InstructionsContainer>

          <Footer>
            <SafeAreaView>
              <PreviewButtonsRow>
                <PreviewButton onPress={handleRetake} variant="secondary">
                  <PreviewButtonIcon name="refresh-cw" />
                  <PreviewButtonText>Tirar outra</PreviewButtonText>
                </PreviewButton>

                <PreviewButton onPress={handleConfirmPhoto} variant="primary">
                  <PreviewButtonIcon name="check" />
                  <PreviewButtonText>Usar esta foto</PreviewButtonText>
                </PreviewButton>
              </PreviewButtonsRow>
            </SafeAreaView>
          </Footer>
        </PreviewOverlay>
      </Container>
    );
  }

  // Camera mode
  return (
    <Container>
      <EmprestimoEligibilityGate />
      {showSuccess && (
        <SuccessContainer>
          {uploading ? (
            <>
              <ActivityIndicator
                size="large"
                color="#333"
                style={{marginBottom: 16}}
              />
              <SuccessText
                ref={successTextRef}
                animation="fadeIn"
                duration={1500}
                useNativeDriver={true}
                style={{textAlign: 'center', fontSize: 18}}>
                {successMessage}
              </SuccessText>
            </>
          ) : (
            <SuccessText
              ref={successTextRef}
              animation="fadeIn"
              duration={1500}
              useNativeDriver={true}
              style={{textAlign: 'center', fontSize: 18}}>
              {successMessage}
            </SuccessText>
          )}
        </SuccessContainer>
      )}

      <CloseButtonContainer>
        <SafeAreaView>
          <TopControlsRow>
            <CloseButton activeOpacity={1} onPress={() => navigation.goBack()}>
              <CloseButtonIcon name="x" />
            </CloseButton>
          </TopControlsRow>
        </SafeAreaView>
      </CloseButtonContainer>
      <Camera
        key={device?.id}
        ref={cameraRef}
        device={device}
        isActive={isFocused}
        photo={true}
        torch="off"
        onInitialized={() => setIsCameraReady(true)}
        style={{flex: 1}}
      />
      {!isCameraReady && (
        <ActivityIndicator
          size="large"
          color="#fff"
          style={{position: 'absolute', top: 0, bottom: 0, left: 0, right: 0}}
        />
      )}

      <ContainerCamera
        onStartShouldSetResponder={() => true}
        onResponderRelease={handleFocus}>
        {currentStep === 'selfie' ? (
          <MaskImage source={faceMask} />
        ) : (
          <MaskImage source={identMask} />
        )}

        {focusPoint && (
          <FocusIndicator
            style={{top: focusPoint.y - 30, left: focusPoint.x - 30}}
          />
        )}

        <InstructionsContainer>
          <Instruction>{config.instruction}</Instruction>
        </InstructionsContainer>

        <Footer>
          <SafeAreaView
            style={{justifyContent: 'center', alignItems: 'center'}}>
            <CameraFooterRow>
              <CaptureButton onPress={takePicture}>
                <CaptureButtonCircle>
                  <CaptureButtonIcon name="camera" />
                </CaptureButtonCircle>
                <CaptureInstruction>Tirar foto</CaptureInstruction>
              </CaptureButton>
            </CameraFooterRow>
          </SafeAreaView>
          {currentStep !== 'selfie' && (
            <UploadButton onPress={pickFromGallery}>
              <UploadButtonCircle>
                <UploadButtonIcon name="image" />
              </UploadButtonCircle>
            </UploadButton>
          )}
        </Footer>
      </ContainerCamera>
    </Container>
  );
}
